/* eslint-disable @typescript-eslint/no-explicit-any */

import { Dayjs } from 'dayjs';

export type GreeksData = number[][];

// keys in this map are strikes, value is 2d array of put/call data
export type RawGreeksObject = {
  [strike: number]: GreeksData;
};

// keys are expiration dates
export type RawGreeksDataMap = {
  [exp: number]: RawGreeksObject;
};

export type RawStatsObject = {
  [delta: number]: number[];
};

export type RawStatsData = {
  [tte: number]: RawStatsObject;
};

export type RawStatsDataMap = {
  [lookback: number]: RawStatsData;
};

export enum StrikeDisplayType {
  FixedStrike = 'Strike',
  Delta = 'Delta',
  Moneyness = '% Moneyness',
}

export enum ExpirationsDisplayType {
  ExpirationDate = 'Expiration Date',
  DaysToExpiration = 'Days to Expiration',
}

export enum VolSkewReferenceLine {
  CurrentIV = 'current_iv',
  MinIV = 'min_iv',
  MaxIV = 'max_iv',
  AvgIV = 'avg_iv',
  MedianIV = 'median_iv',
  P10_IV = 'p10_iv',
  P25_IV = 'p25_iv',
  P75_IV = 'p75_iv',
  P90_IV = 'p90_iv',
}

export interface IVolSettingsProps {
  selectedSym: string;
}
export type VolSkewKey = 'delta' | 'delta-x' | 'strike' | 'moneyness';

export const GREEK_IDX = {
  BID: 0,
  ASK: 1,
  DELTA: 2,
  GAMMA: 3,
  THETA: 4,
  VEGA: 5,
  VOLATILITY: 6,
  TIMESTAMP: 7,
};

export const OPTION_IDX = {
  CALL: 0,
  PUT: 1,
};

export const STAT_IDX = {
  MIN: 0,
  MAX: 1,
  P10: 2,
  P25: 3,
  P75: 4,
  P90: 5,
  MEAN: 6,
  MEDIAN: 7,
  STDEV: 8,
};

export const CANDLE_IDX = {
  TIMESTAMP_OPEN: 0,
  OPEN: 1,
  HIGH: 2,
  LOW: 3,
  CLOSE: 4,
  VOLUME: 5,
};

/// candle tuple with fields in this order:
///   time: number -- unix timestamp in ms
///   open: number
///   high: number
///   low: number
///   close: number
///   volume: number
export type CandleTupleTOHLCV = [
  number,
  number,
  number,
  number,
  number,
  number,
];

/// candle tuple with fields in this order:
///   symbol: string
///   time: number -- unix timestamp in ms
///   open: number
///   high: number
///   low: number
///   close: number
export type CandleTupleSTOHLC = [
  string,
  number,
  number,
  number,
  number,
  number,
];

export interface CandleSTOHLC {
  symbol: string;
  time: number; // unix timestamp in ms
  open: number;
  high: number;
  low: number;
  close: number;
}

export function candleSTOHLCfromTuple(tup: CandleTupleSTOHLC): CandleSTOHLC {
  const [symbol, time, open, high, low, close] = tup;
  return {
    symbol,
    time,
    open,
    high,
    low,
    close,
  };
}

export type Point = {
  x: number;
  y: number;
};

export enum GreeksStatsLookback {
  THIRTY = '30',
  SIXTY = '60',
  NINETY = '90',
}

export enum IVolTab {
  FixedStrikeMatrix = 'fixed_strike_matrix',
  TermStructure = 'term_structure',
  VolSkew = 'vol_skew',
  Vix = 'vix',
}

export const IVOL_LABEL_MAPPING = new Map([
  [IVolTab.FixedStrikeMatrix, 'Fixed Strike Matrix'],
  [IVolTab.TermStructure, 'Term Structure'],
  [IVolTab.VolSkew, 'Volatility Skew'],
  [IVolTab.Vix, 'VIX Term Structure'],
]);

export const IVOL_TABS: Set<string> = new Set(
  Object.values(IVolTab).map((s) => s as string),
);

export enum MatrixDataMode {
  SKEW_PREMIUM_MODE = 'Skew Premium',
  STATS_MODE = 'Statistical Mode',
  COMPARE_MODE = 'Compare Mode',
  DEFAULT = 'Implied Vol Mode',
}

export interface TermStructure {
  // days to expiry, on x-axis
  daysToExpiry: number;
  // expiration date, on x-axis
  expirationDate: number;
  // dynamic number of key/value pairs ranging from timePeriods to statistical numbers
  [key: string]: any;
}

// representation of raw daily greeks data
export type DailyGreeks = {
  key: string;
  tradeDate: Dayjs;
  data: RawGreeksDataMap;
  currentPrice: number;
  color: string;
  // When set, renders this line as-is instead of fetching `data`. Rows must be keyed by tradeDate.format('YYYY-MM-DD').
  precomputedTermStructure?: TermStructure[];
  // Overrides the tradeDate-derived legend/tooltip name - needed for a precomputed line with no real trade date.
  legendLabel?: string;
};

export interface VolSkew {
  // strike price, on x-axis
  strike: number;
  // moneyness, on x-axis
  moneyness: number;

  'delta-x': number;
  // dynamic number of key/value pairs ranging from timePeriods to statistical numbers
  [key: string]: any;
}

// Represents the raw greeks strike data associated with a specific trade and expiry date pair
export type DatedGreeks = {
  key: string;
  sym: string;
  tradeDate: Dayjs;
  expiryDate: string;
  greeksMap: RawGreeksDataMap;
  currentPrice: number;
  statsMap: RawStatsObject;
  color: string;
  // When set, VolSkewChart renders this line as-is instead of fetching
  // and extracting greeksMap/statsMap. Same gotcha as
  // DailyGreeks.precomputedTermStructure: the chart recomputes `key` as
  // `${tradeDate.format('YYYY-MM-DD')}-${expiryDate}` (see
  // getVolSkewTimeframeString) rather than reusing this entry's own
  // `key` - rows must be keyed by that computed string, or the line
  // silently renders with zero points.
  precomputedVolSkew?: VolSkew[];
  // Overrides the legend/tooltip name for this line - see
  // DailyGreeks.legendLabel for the same reasoning.
  legendLabel?: string;
};

export enum FixedStrikeZoomLevel {
  Normal = 'Normal',
  ZoomedOut = 'Zoomed Out',
}

export interface FixedStrikeCellZoomProps {
  expColumnWidth: number;
  cellColumnWidth: number;
  height: number;
  fontSize: string;
  transform: string;
}

export enum MatrixExpirationsRange {
  '3months' = 3,
  '6months' = 6,
  '9months' = 9,
  '12months' = 12,
  all = 'All Expirations',
}

export const MATRIX_EXPIRATIONS_RANGE_MAPPING = new Map([
  [MatrixExpirationsRange['3months'], '3 Months'],
  [MatrixExpirationsRange['6months'], '6 Months'],
  [MatrixExpirationsRange['9months'], '9 Months'],
  [MatrixExpirationsRange['12months'], '12 Months'],
  [MatrixExpirationsRange.all, 'All Expirations'],
]);

export enum MatrixPercentOTMRange {
  '5percent' = 0.05,
  '10percent' = 0.1,
  '15percent' = 0.15,
  '20percent' = 0.2,
  all = 'All Strikes',
}

export const MATRIX_PERCENT_OTM_RANGE_MAPPING = new Map([
  [MatrixPercentOTMRange['5percent'], '5%'],
  [MatrixPercentOTMRange['10percent'], '10%'],
  [MatrixPercentOTMRange['15percent'], '15%'],
  [MatrixPercentOTMRange['20percent'], '20%'],
  [MatrixPercentOTMRange.all, 'All Strikes'],
]);

export enum PrunePercentRange {
  none = 'None',
  'low' = 0.25,
  'medium' = 0.5,
  'high' = 0.75,
}

export const PRUNE_PERCENT_MAPPING = new Map([
  [PrunePercentRange.none, 'None'],
  [PrunePercentRange['low'], 'Low'],
  [PrunePercentRange['medium'], 'Medium'],
  [PrunePercentRange['high'], 'High'],
]);

export interface MatrixData {
  id: number | string;
  expiryDate: number;
  [key: string]: any;
}

export interface MatrixColumnData {
  dataKey: keyof MatrixData;
  label: string;
  numeric?: boolean;
  styling?: any;
}

export interface MatrixTooltipInfo {
  label: string;
  value: string;
  style?: any;
}

export interface SkewPrem {
  strike: number | null;
  value: number | null;
}

export interface MatrixSkewPrem {
  putMax: SkewPrem;
  callMax: SkewPrem;
  totalMax: SkewPrem;
}

export type CellValueFormat = 'absolute' | 'currency' | 'percentage';

export const MATRIX_MODE_LABEL_MAPPING = new Map([
  [MatrixDataMode.COMPARE_MODE, 'Implied Vol Time-Scaled Difference'],
  [MatrixDataMode.STATS_MODE, 'Implied Vol Z-Score'],
  [MatrixDataMode.SKEW_PREMIUM_MODE, 'Vol Skew Premium'],
]);

export const MATRIX_MODE_VALUE_LABEL_MAPPING = new Map([
  [MatrixDataMode.COMPARE_MODE, 'Implied Vol Difference'],
  [MatrixDataMode.SKEW_PREMIUM_MODE, 'Implied Vol'],
  [MatrixDataMode.STATS_MODE, 'Implied Vol'],
  [MatrixDataMode.DEFAULT, 'Implied Vol'],
]);

export interface SelectedDateLine {
  key: string;
  tradeDate: Dayjs;
  color: string;
  notDisplayed?: boolean;
}

export interface TermStructureConfig {
  displayType: ExpirationsDisplayType;
  showEconomicEvents: boolean;
  showForwardIVs: boolean;
  showStats: boolean;
  showDividends: boolean;
  showZoomBrush: boolean;
  useSpotGammaIV: boolean;
  statsLookback: GreeksStatsLookback;
  /** Extra trade dates the user added (YYYY-MM-DD), beyond today's default */
  additionalDates: Omit<DailyGreeks, 'data'>[];
  /** Date keys whose chart lines are currently toggled off */
  hiddenDates: string[];
  /** Reference lines for dates outside the real/comparison lines, e.g. a strategy's own expirations */
  markedExpirations: { date: number; label: string; color?: string }[];
  /**
   * Fills in each visible line's tooltip value at every point via linear
   * interpolation between its own two nearest real neighbors (never
   * extrapolated past its real range, so the line itself still only draws
   * where real data exists). Off by default - only meaningful for
   * precomputed lines being compared point-by-point; real comparison dates
   * shouldn't show invented numbers where no data exists.
   */
  interpolateTooltipValues?: boolean;
}

export const DEFAULT_TERM_STRUCTURE_CONFIG: TermStructureConfig = {
  displayType: ExpirationsDisplayType.ExpirationDate,
  showEconomicEvents: true,
  showForwardIVs: false,
  showStats: true,
  showDividends: false,
  showZoomBrush: true,
  useSpotGammaIV: false,
  statsLookback: GreeksStatsLookback.SIXTY,
  additionalDates: [],
  hiddenDates: [],
  markedExpirations: [],
};

/** JSON-safe persisted shape of one vol-skew line (greeks are re-fetched on load). */
export interface PersistedVolSkewLine {
  key: string;
  tradeDate: string;
  expiryDate: string;
  color: string;
}

export interface VolSkewConfig {
  strikeDisplay: StrikeDisplayType;
  showStats: boolean;
  showDividends: boolean;
  showSkewDiff: boolean;
  showZoomBrush: boolean;
  useSpotGammaIV: boolean;
  statsLookback: GreeksStatsLookback;
  /** Every line on the chart; persisted for `asOfDate`, reset on a new day. */
  lines: PersistedVolSkewLine[];
  /** Line keys currently toggled off. */
  hiddenKeys: string[];
  /** Session day (`getQueryDate(true)`) `lines` belong to; a mismatch reseeds the default. */
  asOfDate?: string;
  /** Strikes to mark on the chart (e.g. a strategy's trade legs) - caller-supplied, not persisted. */
  markedStrikes: { strike: number; label: string; color?: string }[];
}

export const DEFAULT_VOL_SKEW_CONFIG: VolSkewConfig = {
  strikeDisplay: StrikeDisplayType.FixedStrike,
  showStats: true,
  showDividends: false,
  showSkewDiff: true,
  showZoomBrush: true,
  useSpotGammaIV: false,
  statsLookback: GreeksStatsLookback.NINETY,
  lines: [],
  hiddenKeys: [],
  asOfDate: undefined,
  markedStrikes: [],
};

export interface VixConfig {
  showEconomicEvents: boolean;
  displayType: ExpirationsDisplayType;
  /** Extra trade dates beyond the auto-initialized today + prevBusinessDay */
  additionalDates: SelectedDateLine[];
}

export const DEFAULT_VIX_CONFIG: VixConfig = {
  showEconomicEvents: true,
  displayType: ExpirationsDisplayType.ExpirationDate,
  additionalDates: [],
};

export interface VixCombinedChartData {
  daysToExpiry: number;
  expirationDate: number;
  expirationDayjs: Dayjs;
  quoteDate: string;
  quoteDayjs: Dayjs;
  close: number;
  high: number;
  low: number;
  open: number;
  sym: string;
  time: string;
  volume?: number | null;
  expiration_date: string;
}

export interface FixedStrikeConfig {
  dataMode: MatrixDataMode;
  zoomLevel: FixedStrikeZoomLevel;
  expirationRange: MatrixExpirationsRange;
  percentOTMRange: MatrixPercentOTMRange;
  pruneRowsPercent: PrunePercentRange;
  pruneColumnsPercent: PrunePercentRange;
  showHistoricalData: boolean;
  showHighlights: boolean;
  showDividends: boolean;
  useSpotGammaIV: boolean;
  showZScoreColors: boolean;
  statsLookback: GreeksStatsLookback;
}

export const DEFAULT_FIXED_STRIKE_CONFIG: FixedStrikeConfig = {
  dataMode: MatrixDataMode.STATS_MODE,
  zoomLevel: FixedStrikeZoomLevel.Normal,
  expirationRange: MatrixExpirationsRange['9months'],
  percentOTMRange: MatrixPercentOTMRange['10percent'],
  pruneRowsPercent: PrunePercentRange.medium,
  pruneColumnsPercent: PrunePercentRange.medium,
  showHistoricalData: false,
  showHighlights: false,
  showDividends: false,
  useSpotGammaIV: false,
  showZScoreColors: true,
  statsLookback: GreeksStatsLookback.NINETY,
};
